import { OrderUnitOfMeasure, RateType } from '@treadinc/horizon-api-spec';

import { ContactItem } from '~hooks/useContact/models/ContactItem';
import { Order } from '~hooks/useOrders/models';
import { Project } from '~hooks/useProjects/models';
import {
  Rate,
  RateBasicWithValue,
  RateCommissionInputs,
  RateFlatCommissionInputs,
  RateFlatRateInputs,
  RatePerUnitInputs,
  RateTimeInputs,
} from '~hooks/useRates/models';
import { SiteRateSchedule } from '~hooks/useSiteRateSchedule/models';
import { Site, SiteBasic, WayPoint } from '~hooks/useSites/models';
import { Nullable } from '~types/Nullable';
import { nullableStringOrNumberIsValidNumber } from '~utils/utilFunctions';

import { DEFAULT_EQUIPMENT_TYPE_GROSS_CAPACITY } from '../newOrderFormSchema';
import { AddOnAssignmentSchema } from './components/AutomaticAddOns';
import { RateDetailsDTO } from './schema';

export const createWaypoint = (
  site: SiteBasic | Site,
  type: string,
  ordinality: number,
  waypointID?: string,
  contact?: ContactItem,
) => {
  return {
    id: waypointID,
    type,
    ordinality,
    contact: {
      name: contact?.name ?? '',
      phone: contact?.phone ?? '',
    } as unknown as Nullable<ContactItem>,
    site: {
      id: site.id,
      name: site.name,
      lat: site.lat,
      lon: site.lon,
      address: (site as Site).address,
      discardedAt: site.discardedAt?.toISOString() ?? null,
    },
  } as unknown as WayPoint;
};

export const rateToRateBasicWithValue = (rate: Rate) => {
  let rateValue;

  if (rate.isCommission) {
    rateValue = (rate.rateInputs as RateCommissionInputs).commissionPercentage;
  } else if (rate.isTimeBased) {
    rateValue = (rate.rateInputs as RateTimeInputs).timeRate;
  } else if (rate.isFlatCommission) {
    rateValue = (rate.rateInputs as RateFlatCommissionInputs).commissionAmount;
  } else if (rate.isFlatRate) {
    rateValue = (rate.rateInputs as RateFlatRateInputs).flatAmount;
  } else {
    rateValue = (rate.rateInputs as RatePerUnitInputs).perUnitRate;
  }

  return new RateBasicWithValue(
    String(rate.id),
    rate.name,
    rate.type.value,
    rateValue,
    rate.addOns,
  );
};

export function deriveDriverRateFromVendorRate(
  vendorRateDetails?: RateDetailsDTO,
): RateDetailsDTO | undefined {
  // When the vendor rate was not user-modified, propagate `undefined` so the
  // deparse layer omits the `driver_rate_*` fields. Returning a default
  // `{nulls}` object here would clobber values written by the Site Rate
  // Schedule cascade (null !== undefined at the deparse-layer guard).
  if (!vendorRateDetails) {
    return undefined;
  }

  const driverRate: RateDetailsDTO = {
    targetRate: null,
    targetRateType: null,
    targetRateValue: null,
  };

  const vendorRateCard = vendorRateDetails?.targetRate;

  if (vendorRateCard) {
    driverRate.targetRateType = {
      label: vendorRateCard.type,
      value: vendorRateCard.type,
    };
    driverRate.targetRateValue = vendorRateCard.rate;
  } else {
    const rateType = vendorRateDetails?.targetRateType?.value ?? null;
    const rateValue = vendorRateDetails?.targetRateValue ?? null;

    driverRate.targetRateType = rateType ? { label: rateType, value: rateType } : null;
    driverRate.targetRateValue = rateValue;
  }

  return driverRate;
}

export const getOrderFormRate = (
  defaultOrder: Order | null,
  project: Project | null,
  fieldType: 'customer' | 'vendor' | 'driver',
  projectSiteRates?: SiteRateSchedule | null,
  primaryCustomerRate?: RateBasicWithValue | null,
  applyFallbacks: boolean = true,
  preferSiteRateOverOrder: boolean = false,
) => {
  const siteRateValue = projectSiteRates?.[`${fieldType}RateValue`];
  const hasDefinedSiteRate =
    projectSiteRates?.[`${fieldType}Rate`] ||
    projectSiteRates?.[`${fieldType}RateType`] ||
    (siteRateValue !== null && siteRateValue !== undefined);

  const orderRateValue = defaultOrder?.[`${fieldType}RateValue`];
  const hasDefinedOrderRate =
    defaultOrder?.[`${fieldType}Rate`] ||
    defaultOrder?.[`${fieldType}RateType`] ||
    (orderRateValue !== null && orderRateValue !== undefined);

  // When editing an order whose project has a matched SRS for the current
  // pickup, mirror the SRS for this field — including `null` so an empty SRS
  // field clears the matching form value. Without this, switching from a
  // pickup with full rates to one with a partially-empty rule would leave
  // the previous (now-stale) value on the form. Falls through to the legacy
  // chain only when no SRS row matched (i.e. `projectSiteRates` is absent).
  if (preferSiteRateOverOrder && projectSiteRates) {
    return projectSiteRates?.[`${fieldType}Rate`] ?? null;
  }

  if (hasDefinedOrderRate) {
    return defaultOrder?.[`${fieldType}Rate`] ?? null;
  }

  if (hasDefinedSiteRate) {
    return projectSiteRates?.[`${fieldType}Rate`] ?? null;
  }

  if (!applyFallbacks) {
    return null;
  }

  // Check if project has ANY rate info defined (rate card, type, or value)
  const projectRateValue = project?.[`${fieldType}RateValue`];
  const hasDefinedProjectRate =
    project?.[`${fieldType}Rate`] ||
    project?.[`${fieldType}RateType`] ||
    (projectRateValue !== null && projectRateValue !== undefined);

  if (hasDefinedProjectRate) {
    return project?.[`${fieldType}Rate`] ?? null;
  }

  // Fall back to primary customer rate (only for customer field type)
  if (fieldType === 'customer' && primaryCustomerRate) {
    return primaryCustomerRate;
  }

  return null;
};

export const getOrderFormRateType = (
  defaultOrder: Order | null,
  project: Project | null,
  fieldType: 'customer' | 'vendor' | 'driver',
  projectSiteRates?: SiteRateSchedule | null,
  primaryCustomerRate?: RateBasicWithValue | null,
  applyFallbacks: boolean = true,
  preferSiteRateOverOrder: boolean = false,
): RateType | null => {
  const siteRateValue = projectSiteRates?.[`${fieldType}RateValue`];
  const hasDefinedSiteRate =
    projectSiteRates?.[`${fieldType}Rate`] ||
    projectSiteRates?.[`${fieldType}RateType`] ||
    (siteRateValue !== null && siteRateValue !== undefined);

  const orderRateValue = defaultOrder?.[`${fieldType}RateValue`];
  const hasDefinedOrderRate =
    defaultOrder?.[`${fieldType}Rate`] ||
    defaultOrder?.[`${fieldType}RateType`] ||
    (orderRateValue !== null && orderRateValue !== undefined);

  if (preferSiteRateOverOrder && projectSiteRates) {
    return projectSiteRates?.[`${fieldType}RateType`] ?? null;
  }

  if (hasDefinedOrderRate) {
    return defaultOrder?.[`${fieldType}RateType`] ?? null;
  }

  if (hasDefinedSiteRate) {
    return projectSiteRates?.[`${fieldType}RateType`] ?? null;
  }

  if (!applyFallbacks) {
    return null;
  }

  // Check if project has ANY rate info defined (rate card, type, or value)
  const projectRateValue = project?.[`${fieldType}RateValue`];
  const hasDefinedProjectRate =
    project?.[`${fieldType}Rate`] ||
    project?.[`${fieldType}RateType`] ||
    (projectRateValue !== null && projectRateValue !== undefined);

  if (hasDefinedProjectRate) {
    return project?.[`${fieldType}RateType`] ?? null;
  }

  // Fall back to primary customer rate type (only for customer field type)
  if (fieldType === 'customer' && primaryCustomerRate) {
    return primaryCustomerRate.type as RateType;
  }

  return null;
};

export const getOrderFormRateValue = (
  defaultOrder: Order | null,
  project: Project | null,
  fieldType: 'customer' | 'vendor' | 'driver',
  projectSiteRates?: SiteRateSchedule | null,
  primaryCustomerRate?: RateBasicWithValue | null,
  applyFallbacks: boolean = true,
  preferSiteRateOverOrder: boolean = false,
): number | null => {
  const siteRateValue = projectSiteRates?.[`${fieldType}RateValue`];
  const hasDefinedSiteRate =
    projectSiteRates?.[`${fieldType}Rate`] ||
    projectSiteRates?.[`${fieldType}RateType`] ||
    (siteRateValue !== null && siteRateValue !== undefined);

  const orderRateValue = defaultOrder?.[`${fieldType}RateValue`];
  const hasDefinedOrderRate =
    defaultOrder?.[`${fieldType}Rate`] ||
    defaultOrder?.[`${fieldType}RateType`] ||
    (orderRateValue !== null && orderRateValue !== undefined);

  if (preferSiteRateOverOrder && projectSiteRates) {
    return projectSiteRates?.[`${fieldType}RateValue`] ?? null;
  }

  if (hasDefinedOrderRate) {
    return defaultOrder?.[`${fieldType}RateValue`] ?? null;
  }

  if (hasDefinedSiteRate) {
    return projectSiteRates?.[`${fieldType}RateValue`] ?? null;
  }

  if (!applyFallbacks) {
    return null;
  }

  // Check if project has ANY rate info defined (rate card, type, or value)
  const projectRateValue = project?.[`${fieldType}RateValue`];
  const hasDefinedProjectRate =
    project?.[`${fieldType}Rate`] ||
    project?.[`${fieldType}RateType`] ||
    (projectRateValue !== null && projectRateValue !== undefined);

  if (hasDefinedProjectRate) {
    return project?.[`${fieldType}RateValue`] ?? null;
  }

  // Fall back to primary customer rate value (only for customer field type)
  if (fieldType === 'customer' && primaryCustomerRate) {
    return primaryCustomerRate.rate;
  }

  return null;
};

/**
 * Reconcile the current form's add-on assignments with an incoming cascade
 * override (e.g. site-rate or project-level auto add-ons).
 *
 * When the matching condition behind a cascade changes (e.g. the user changes
 * the pickup site, which swaps the applicable freight rule), the rates and
 * add-ons that came from the previous source should be replaced wholesale:
 *
 *  - All previously-inherited entries (those with `originalAutoAddOnAssignmentId`,
 *    or whose form-state `id` is listed in `inheritedIds`) are replaced.
 *    Persisted ones are marked `isDeleted: true` so the backend removes them;
 *    `isNew` stale cascades are dropped entirely (never persisted).
 *  - User-added custom add-ons are preserved.
 *  - The incoming cascade is appended at the end.
 *
 * This prevents duplicate add-ons: when Site A's cascade is replaced by Site
 * B's, Site A's inherited entries are cleaned up instead of lingering
 * alongside Site B's new entries.
 *
 * `inheritedIds` exists because copy-and-edit deliberately loads source-order
 * add-ons with `originalAutoAddOnAssignmentId: null` — the new order must not
 * be persisted with a link back to the source's assignment record. Without
 * this set, source-loaded entries would look indistinguishable from user-added
 * entries and survive every pickup-site change.
 */
export const reconcileAddOnsWithOverride = (
  previous: AddOnAssignmentSchema[],
  incoming: AddOnAssignmentSchema[],
  inheritedIds: ReadonlySet<string> = new Set(),
): AddOnAssignmentSchema[] => {
  const reconciled = previous.flatMap((entry) => {
    if (entry.isDeleted) return [entry];

    const entryId = entry.addOn?.id;
    const wasInherited =
      Boolean(entry.addOn?.originalAutoAddOnAssignmentId) ||
      Boolean(entryId && inheritedIds.has(entryId));
    if (!wasInherited) {
      // User-added custom — keep as-is
      return [entry];
    }

    // Local-only stale cascade (never persisted) — drop it entirely
    if (entry.isNew) return [];

    // Persisted inherited entry — tell the backend to remove it
    return [{ ...entry, isDeleted: true }];
  });

  return [...reconciled, ...incoming];
};

/**
 * Determines if recurring loads should be enabled based on quantity vs equipment capacity
 * @param quantity - The job quantity
 * @param equipmentGrossCapacity - The equipment gross capacity
 * @param unitOfMeasure - The job unit of measure
 * @returns true if recurring loads should be enabled, false otherwise
 */
export const shouldEnableRecurringLoads = (
  quantity: string | number | null,
  equipmentGrossCapacity: string | number | null,
  unitOfMeasure?: string | null,
): boolean => {
  if (unitOfMeasure === OrderUnitOfMeasure.HOUR) {
    return true;
  }

  const isValidQuantity = nullableStringOrNumberIsValidNumber(quantity);
  const isValidEquipmentTypeGrossCapacity =
    nullableStringOrNumberIsValidNumber(equipmentGrossCapacity);

  if (!isValidQuantity) {
    return true;
  }

  const quantityValue = Number(quantity);
  const maxCapacity = isValidEquipmentTypeGrossCapacity
    ? Number(equipmentGrossCapacity)
    : DEFAULT_EQUIPMENT_TYPE_GROSS_CAPACITY;

  return quantityValue > maxCapacity;
};

export type RatesSyncStatus = 'synced' | 'stamped' | null;
type SiteRateScheduleTarget = 'project' | 'order';

/**
 * Selective rate apply: does the order actually OWN its rate cards (a real "stamp")?
 *
 * Order-scoped schedules come in three species, and only two of them count as ownership:
 * - A completion stamp (`pinnedAt` null, `dereferenced` false) — the order owns it.
 * - A dereference-clone (`dereferenced` true) — the snapshot created when a project rate
 *   edit durably skipped this order ("Apply rate changes to existing orders"
 *   unchecked). The order owns it: the FE must display and edit this snapshot, never the
 *   project's live rate book.
 * - A pin-clone (`pinnedAt` set, `dereferenced` false) — a snapshot holding the order's
 *   rates against a single skipped rate-change cascade. NOT ownership: a pinned order
 *   stays sync-eligible (its chip reads "synced" and its edits still route to the
 *   project).
 *
 * Centralizing this predicate is the fix for that risk — every "does this order own
 * rates" check must exclude pin-clones while keeping dereference-clones.
 */
export const orderOwnsSiteRates = (
  schedules:
    | ReadonlyArray<{ dereferenced?: boolean; pinnedAt?: unknown }>
    | null
    | undefined,
): boolean =>
  (schedules ?? []).some((schedule) => schedule.dereferenced || !schedule.pinnedAt);

/**
 * Picks the chip status for the Update Order form's Freight Rates section.
 * Order matters: a stamped (decoupled) order wins over project-synced.
 * Returns null when neither owner has rates. Completion is deliberately not a
 * factor: completed orders resolve by the same ownership rules as any other
 * order.
 */
export const resolveRatesSyncStatus = ({
  orderHasOwnSiteRates,
  projectId,
  projectHasSiteRates,
}: {
  orderHasOwnSiteRates: boolean;
  projectId: string | null | undefined;
  projectHasSiteRates: boolean;
}): RatesSyncStatus => {
  if (orderHasOwnSiteRates) return 'stamped';
  if (projectId && projectHasSiteRates) return 'synced';
  return null;
};

/**
 * Picks where pending SRS write ops should land. A stamped (decoupled) order
 * always routes to the order endpoint, even when a project is attached, so
 * order-specific rate changes don't bleed into the project's shared records.
 * Otherwise the project endpoint wins when one is attached; orderless flows
 * fall back to the order endpoint.
 *
 * `dereferenceFromProject` is the order form's unchecked apply-rate-changes
 * box: the save decouples the order first, so its writes land on the copies
 * the decoupling minted and the project's own cards are never touched.
 */
export const resolveSiteRateScheduleTarget = ({
  dereferenceFromProject,
  orderHasOwnSiteRates,
  projectId,
}: {
  dereferenceFromProject?: boolean;
  orderHasOwnSiteRates: boolean;
  projectId: string | null | undefined;
}): SiteRateScheduleTarget => {
  if (orderHasOwnSiteRates) return 'order';
  if (projectId) return dereferenceFromProject ? 'order' : 'project';
  return 'order';
};
